import { FhirResourceTypes } from '@oh-vcp/components-common';
import { useState, useEffect, useMemo } from 'react';
import useSmartOnFhirResource, { fetchSmartOnFhirData } from '../services/useSmartOnFhirResource';
import { useQueries } from 'react-query';

type SupportingRef = {
  resourceType: string;
  id: string;
};

type ResourceMap = Partial<Record<string, unknown>>;

const useFhirContext = (fhirContext: string | undefined) => {
  const [serviceRequestId, setServiceRequestId] = useState<string | undefined>();
  const [supportingRefs, setSupportingRefs] = useState<SupportingRef[]>([]);

  const { data: smartOnFhirContextDetails } = useSmartOnFhirResource(
    FhirResourceTypes.ServiceRequest,
    serviceRequestId
  );

  useEffect(() => {
    if (!fhirContext) return;
    const fhirContextObj = JSON.parse(fhirContext);
    if (fhirContextObj && fhirContextObj.length > 0 && fhirContextObj[0].reference) {
      const [, id] = fhirContextObj[0].reference.split('/');
      setServiceRequestId(id);
    }
  }, [fhirContext]);

  useEffect(() => {
    if (!smartOnFhirContextDetails?.supportingInfo) return;

    const refs: SupportingRef[] = smartOnFhirContextDetails.supportingInfo.map(
      ({ reference }: { reference: string }) => {
        const [resourceType, id] = reference.split('/');
        return { resourceType: resourceType, id };
      }
    );

    setSupportingRefs(refs);
  }, [smartOnFhirContextDetails]);

  const results = useQueries(
    supportingRefs.map(({ resourceType, id }) => ({
      queryKey: ['fhirResource', resourceType, id],
      queryFn: () => fetchSmartOnFhirData(resourceType, id),
      enabled: !!id,
      staleTime: Infinity,
      refetchOnWindowFocus: false
    }))
  );

  // results from useQueries is always a new array reference on every render (React Query v3
  // behaviour). Using it directly as a useMemo dep would cause resourceMap to be recreated on
  // every render. Instead we use a stable fingerprint based on dataUpdatedAt so the memo only
  // recomputes when query data actually changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resultsDataKey = results.map((r) => r.dataUpdatedAt ?? 0).join(':');

  const resourceMap: ResourceMap = useMemo(
    () =>
      results.reduce<ResourceMap>((acc, result, index) => {
        const { resourceType } = supportingRefs[index];
        if (result.data) {
          const existing = acc[resourceType];
          acc[resourceType] = existing
            ? ([] as unknown[]).concat(existing, result.data)
            : result.data;
        }
        return acc;
      }, {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resultsDataKey, supportingRefs]
  );

  const isLoading = results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);

  const notes: unknown[] = smartOnFhirContextDetails?.note ?? [];

  return { resourceMap, isLoading, isError, notes };
};

export default useFhirContext;
